import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "backdrop",
    "modal",
    "form",
    "formContainer",
    "name",
    "email",
    "submit",
    "error",
    "success",
    "successMessage"
  ]

  connect() {
    this.isOpen = false
  }

  open(event) {
    event?.preventDefault()
    this.resetState()
    this.show()
  }

  close(event) {
    event?.preventDefault()
    this.hide()
  }

  closeOnBackdrop(event) {
    if (event.target === this.backdropTarget) {
      this.close()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.close()
    }
  }

  show() {
    this.isOpen = true
    document.body.style.overflow = "hidden"

    this.backdropTarget.classList.remove("opacity-0", "invisible", "pointer-events-none")
    this.backdropTarget.classList.add("opacity-100", "visible", "pointer-events-auto")

    this.modalTarget.classList.remove("opacity-0", "invisible", "pointer-events-none")
    this.modalTarget.classList.add("opacity-100", "visible", "pointer-events-auto")

    requestAnimationFrame(() => this.emailTarget?.focus())
  }

  hide() {
    this.isOpen = false
    document.body.style.overflow = ""

    this.backdropTarget.classList.add("opacity-0", "invisible", "pointer-events-none")
    this.backdropTarget.classList.remove("opacity-100", "visible", "pointer-events-auto")

    this.modalTarget.classList.add("opacity-0", "invisible", "pointer-events-none")
    this.modalTarget.classList.remove("opacity-100", "visible", "pointer-events-auto")
  }

  async submit(event) {
    event.preventDefault()
    this.setLoading(true)
    this.hideError()

    const payload = {
      name: this.nameTarget?.value?.trim() || "",
      email: this.emailTarget?.value?.trim() || ""
    }

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data?.error || "Something went wrong.")
      }

      this.showSuccess(data?.message)
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.setLoading(false)
    }
  }

  showSuccess(message) {
    this.formContainerTarget.classList.add("hidden")
    this.successTarget.classList.remove("hidden")
    if (message && this.hasSuccessMessageTarget) {
      this.successMessageTarget.textContent = message
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  setLoading(loading) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = loading
    this.submitTarget.classList.toggle("opacity-70", loading)
    this.submitTarget.classList.toggle("cursor-not-allowed", loading)
  }

  resetState() {
    this.formContainerTarget.classList.remove("hidden")
    this.successTarget.classList.add("hidden")
    this.hideError()
    if (this.hasNameTarget) this.nameTarget.value = ""
    if (this.hasEmailTarget) this.emailTarget.value = ""
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
